import type { UserRole } from '@/lib/auth/models/auth.model';

/**
 * Cosa il builder puo' mostrare e fare, deciso in UN punto.
 *
 * Il builder e' montato da piu' flussi — la pratica dell'advisor, il template, il
 * viaggiatore autenticato, la proposta condivisa anonima — e fino a questo modulo ogni
 * componente ricostruiva da se' la propria idea di "sola lettura" o "vista cliente"
 * (isReadOnly, isTraveler, isClientView, `userRole === 'traveler'` letto da useAuth...).
 * Le copie divergevano: una card mostrava il costo tramite una strada, il footer
 * nascondeva un pulsante tramite un'altra.
 *
 * Qui c'e' la sola traduzione "chi sei + da dove entri + su cosa" -> capacita'. I
 * componenti leggono le capacita' con useBuilderCapabilities() e non guardano mai il
 * ruolo. Un flusso nuovo e' un caso in piu' di resolveBuilderCapabilities, non un flag
 * in piu' da far scendere per sei livelli di props.
 *
 * NB: e' presentazione, non sicurezza. Chi puo' leggere o scrivere cosa lo decidono RLS
 * e RPC; queste capacita' servono a non mostrare controlli che il backend rifiuterebbe
 * e campi che il cliente non deve vedere.
 */

/** Da dove e' stato aperto il builder. */
export type BuilderEntry =
  /** /itinerary-builder, utente autenticato. */
  | 'workspace'
  /** Proposta condivisa via link: utente anonimo, userRole null. */
  | 'shared';

/** Il pubblico per cui si sta rendendo l'itinerario. */
export type BuilderAudience = 'seller' | 'client';

export interface BuilderCapabilitiesInput {
  entry: BuilderEntry;
  role: UserRole | null | undefined;
  isTemplate: boolean;
  itineraryStatus?: string | null;
  hasTravelRequest?: boolean;
}

export interface BuilderCapabilities {
  entry: BuilderEntry;
  audience: BuilderAudience;
  isTemplate: boolean;

  // --- contenuto --------------------------------------------------------------
  /** Aggiungere, modificare, spostare ed eliminare attivita'; modificare i dati del viaggio. */
  canEdit: boolean;
  /** Costo e link fornitore sulle card delle attivita'. */
  showReservedFields: boolean;
  /** Il selettore dello stato fornitore sulle card. */
  showSupplierStatus: boolean;
  /** Vista iniziale del giorno per giorno. */
  defaultDayView: 'list' | 'timeline';

  // --- economia ----------------------------------------------------------------
  /** Step checkout (markup, policy, piano rate). Un template non ce l'ha. */
  hasCheckout: boolean;
  /** Markup, policy di cancellazione, piano rate, banner di blocco prezzi. */
  showPricing: boolean;
  /** Registrare a mano l'incasso di una rata. */
  canMarkInstallmentPaid: boolean;
  /** Totale costi servizi nel footer: sul template e' l'unico numero economico. */
  showServicesTotalInFooter: boolean;

  // --- azioni del footer --------------------------------------------------------
  /** "Invia al cliente", sull'ultimo step. */
  canSendToClient: boolean;
  /** "Paga" / "Pagato" per il viaggiatore (lo stato del piano resta al footer). */
  canPay: boolean;
  /** "Richiedi modifiche" del viaggiatore. */
  canRequestChanges: boolean;
}

const SENDABLE_STATUSES = new Set(['draft', 'request_changes']);

export const resolveBuilderCapabilities = ({
  entry,
  role,
  isTemplate,
  itineraryStatus,
  hasTravelRequest = false,
}: BuilderCapabilitiesInput): BuilderCapabilities => {
  // Il link condiviso e' sempre vista cliente, qualunque sia il ruolo di chi lo apre:
  // un admin che apre la proposta del cliente deve vedere cio' che vede il cliente.
  const audience: BuilderAudience =
    entry === 'shared' || role === 'traveler' ? 'client' : 'seller';
  const isSeller = audience === 'seller';

  return {
    entry,
    audience,
    isTemplate,

    canEdit: isSeller,
    showReservedFields: isSeller,
    showSupplierStatus: isSeller && !isTemplate,
    defaultDayView: isSeller ? 'list' : 'timeline',

    hasCheckout: isSeller && !isTemplate,
    showPricing: isSeller,
    // Dal 02/09/2026 non e' piu' riservato all'admin: chi gestisce l'itinerario puo'
    // registrare un incasso, in cambio della commissione a debito che lascia.
    canMarkInstallmentPaid: isSeller,
    showServicesTotalInFooter: isSeller && isTemplate,

    // Admin e agenzia supervisionano, non inviano: il preventivo parte dall'advisor.
    canSendToClient:
      isSeller &&
      !isTemplate &&
      role !== 'admin' &&
      role !== 'agency' &&
      hasTravelRequest &&
      SENDABLE_STATUSES.has(itineraryStatus ?? ''),
    // Nella proposta condivisa il pagamento sta nell'header di SharedProposalView.
    canPay: !isSeller && entry === 'workspace',
    canRequestChanges: !isSeller && entry === 'workspace' && itineraryStatus !== 'completed',
  };
};

/**
 * Le capacita' piu' restrittive: vista cliente in sola lettura, nessuna azione.
 * E' il default del Context — un componente montato fuori da un provider deve
 * degradare verso "non mostro niente di riservato", mai verso "posso modificare".
 */
export const RESTRICTED_CAPABILITIES: BuilderCapabilities = resolveBuilderCapabilities({
  entry: 'shared',
  role: null,
  isTemplate: false,
});
